/* eslint-disable no-nested-ternary */
import { PopoverPortal } from "@radix-ui/react-popover";
import type { FilterableIndicator } from "components/screener/v2/ScreenerFilterableIndicators";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "components/ui/Popover";
import { ScrollArea } from "components/ui/ScrollArea";
import { Separator } from "components/ui/Separator";
import classNames from "components/ui/classNames";
import { CheckIcon, CircleIcon, LightbulbIcon, ListFilterIcon, SearchIcon, XIcon } from "lucide-react";
import { useEffect, useReducer, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMediaQuery } from "usehooks-ts";

interface FilterableIndicatorsListProps {
  disabled?: boolean;
  activeFilters: FilterableIndicator[];
  filterableIndicators: FilterableIndicator[];
  onSelectMany: (indicator: FilterableIndicator[]) => void;
}

type State = {
  pendingSelectedFilters: FilterableIndicator[];
  pendingRemovedFilters: string[];
};
type Action =
  | {
      type: "select";
      indicator: FilterableIndicator;
    }
  | {
      type: "remove";
      indicator: FilterableIndicator;
    }
  | {
      type: "reset";
    };

export function FilterableIndicatorsList({
  disabled,
  activeFilters,
  filterableIndicators,
  onSelectMany,
}: FilterableIndicatorsListProps) {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const pointRef = useRef({ x: 0, y: 0 });
  const virtualPopoverRef = useRef({
    getBoundingClientRect: () => DOMRect.fromRect({ width: 0, height: 0, ...pointRef.current }),
  });
  const isMobile = useMediaQuery("(max-width: 639px)");

  const [{ pendingSelectedFilters, pendingRemovedFilters }, dispatch] = useReducer(
    (state: State, action: Action) => {
      if (action.type === "select") {
        return {
          ...state,
          pendingSelectedFilters: state.pendingSelectedFilters
            .filter(({ objectId }) => objectId !== action.indicator.objectId)
            .concat(action.indicator),
          pendingRemovedFilters: state.pendingRemovedFilters.filter(
            (objectId) => objectId !== action.indicator.objectId,
          ),
        };
      }
      if (action.type === "remove") {
        return {
          ...state,
          pendingSelectedFilters: state.pendingSelectedFilters.filter(
            ({ objectId }) => objectId !== action.indicator.objectId,
          ),
          pendingRemovedFilters: state.pendingRemovedFilters.concat(action.indicator.objectId),
        };
      }
      if (action.type === "reset") {
        return {
          pendingSelectedFilters: [],
          pendingRemovedFilters: [],
        };
      }
      return state;
    },
    {
      pendingSelectedFilters: [],
      pendingRemovedFilters: [],
    },
  );

  useEffect(() => {
    if (!isOpen) {
      dispatch({ type: "reset" });
    }
  }, [isOpen]);

  const hasPendingChanges = pendingSelectedFilters?.length !== 0 || pendingRemovedFilters?.length !== 0;

  return (
    <Popover modal={true} open={isOpen} onOpenChange={(isOpen) => setIsOpen(isOpen)}>
      {isOpen && (
        <PopoverPortal>
          <div className="absolute bottom-0 left-0 right-0 top-0 z-10 bg-black/5 backdrop-blur-sm" />
        </PopoverPortal>
      )}
      {isOpen && isMobile ? (
        <PopoverAnchor virtualRef={virtualPopoverRef} />
      ) : (
        <PopoverTrigger asChild={true}>
          <button
            type="button"
            disabled={disabled}
            className={classNames(
              "group flex border border-transparent",
              {
                "z-50": isOpen,
                "items-center gap-x-0.5 rounded-full border-slate-300 bg-slate-100 pr-3 text-slate-600":
                  activeFilters.length === 0,
                "hover:border-blue-400/30 hover:bg-blue-400/20 hover:text-blue-600": activeFilters.length === 0,
                "dark:border-slate-700 dark:bg-sky-400/10 dark:text-sky-200 dark:hover:bg-sky-400/20":
                  activeFilters.length === 0,
              },
              "disabled:pointer-events-none disabled:text-slate-400 disabled:opacity-50",
            )}
          >
            <span
              className={classNames(
                "group flex items-center justify-center rounded-full border",
                "size-8",
                "border-blue-200 bg-blue-100",
                { "dark:border-yellow-400/40 dark:bg-yellow-400/30": activeFilters.length > 0 },
                { "border-transparent bg-transparent": activeFilters.length === 0 },
              )}
            >
              <ListFilterIcon
                absoluteStrokeWidth={true}
                strokeWidth={2}
                className={classNames(
                  "stroke-slate-600 group-hover:stroke-blue-400 dark:stroke-blue-400",
                  {
                    "stroke-blue-600 dark:stroke-yellow-400 dark:group-hover:stroke-yellow-300":
                      activeFilters.length > 0,
                  },
                  "size-5",
                )}
              />
            </span>
            {activeFilters.length === 0 && (
              <strong className="text-sm font-bold">
                <FormattedMessage defaultMessage="Filtrar Colunas" id="zUD+Gz" />
              </strong>
            )}
          </button>
        </PopoverTrigger>
      )}
      <PopoverContent
        className={classNames(
          "relative flex w-80 flex-col gap-y-2 rounded-2xl border border-zinc-300 py-2 shadow-md",
          "dark:border-slate-700",
          {
            "w-80 max-w-xs": !isMobile,
            "m-4 w-[calc(100vw-theme('spacing.8'))]": isMobile,
          },
        )}
        side={isMobile ? "top" : "bottom"}
        align={isMobile ? "start" : "start"}
        sideOffset={isMobile ? 0 : 4}
      >
        <div className="flex items-center gap-x-2 px-1">
          <button
            type="button"
            className={classNames(
              "flex items-center justify-center rounded-full bg-slate-100 p-1",
              "hover:bg-slate-200",
              "focus:outline-none",
              "dark:bg-slate-950",
              "group size-8",
            )}
            onClick={() => setIsOpen(false)}
          >
            <XIcon
              absoluteStrokeWidth={true}
              strokeWidth={3}
              className="size-5 stroke-slate-600 group-hover:stroke-slate-500 dark:stroke-slate-500 dark:group-hover:stroke-slate-300"
            />
          </button>
          <div>
            <h3 className="text-sm font-bold text-slate-800 dark:text-slate-200">
              <FormattedMessage defaultMessage="Indicadores" id="GUCl4s" />
            </h3>
            <p className="text-xs font-bold tracking-wide text-slate-500 dark:text-sky-200">
              <FormattedMessage
                defaultMessage="{totalCount, plural, one {Apenas um} other {# indicadores}}"
                id="vw6og9"
                values={{
                  totalCount: filterableIndicators.length,
                }}
              />
            </p>
          </div>
          <button
            type="button"
            disabled={!hasPendingChanges}
            title={intl.formatMessage({ defaultMessage: "Confirmar Filtros", id: "UkZ+yh" })}
            className={classNames(
              "ml-auto flex items-center justify-center rounded-full p-1 outline-none",
              "bg-viridian-100 hover:bg-viridian-200/80",
              "disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-slate-200/50",
              "dark:bg-lime-600/20",
              "disabled:dark:bg-slate-400/20",
              "dark:hover:bg-lime-600/40",
              "group size-8",
            )}
            onClick={() => {
              if (hasPendingChanges) {
                onSelectMany(
                  activeFilters
                    .filter((candidate) => !pendingRemovedFilters?.includes(candidate.objectId))
                    .concat(pendingSelectedFilters),
                );
                setIsOpen(false);
              }
            }}
          >
            <CheckIcon
              absoluteStrokeWidth={true}
              strokeWidth={2.5}
              className={classNames(
                "stroke-viridian-600",
                "group-disabled:stroke-slate-800",
                "group-hover:stroke-viridian-700",
                "dark:stroke-lime-500 dark:group-hover:stroke-lime-500",
                "size-5",
              )}
            />
          </button>
        </div>
        <Separator />

        <ScrollArea tabIndex={0}>
          {filterableIndicators.length === 0 && (
            <p
              className={classNames(
                "mx-1 flex flex-col items-center justify-center gap-x-0.5 gap-y-2 rounded-xl bg-slate-50 p-4 text-base tracking-tight text-slate-600",
                "dark:bg-slate-800 dark:text-slate-300",
                "font-medium dark:font-bold",
              )}
            >
              <SearchIcon className="inline-flex size-6 fill-zinc-100 stroke-zinc-500 dark:fill-transparent dark:stroke-zinc-300" />
              <FormattedMessage defaultMessage="Nenhum indicador à filtrar" id="oiEPiw" />
            </p>
          )}

          <ul className="flex max-h-96 select-none flex-col gap-y-1 px-1 empty:hidden">
            {filterableIndicators.slice(0, 30).map((node) => {
              const isSelected =
                activeFilters
                  .filter((candidate) => {
                    return !pendingRemovedFilters?.includes(candidate.objectId);
                  })
                  .some((candidate) => candidate.objectId === node.objectId) ||
                pendingSelectedFilters?.some((candidate) => candidate.objectId === node.objectId);
              return (
                <li
                  key={node.objectId}
                  className={classNames("group flex min-h-10 select-none items-center gap-x-1 pr-1 lg:min-h-8")}
                >
                  <button
                    type="button"
                    disabled={false}
                    className={classNames(
                      "group min-h-9 hover:font-bold hover:text-slate-600 lg:min-h-0",
                      "w-full rounded-2xl px-2.5 py-1.5 text-left text-sm font-medium",
                      "flex items-center justify-between",
                      "text-gray-600 hover:bg-blue-50 hover:text-sea-600",
                      "dark:font-bold dark:text-gray-300 dark:hover:bg-orange-400/20 dark:hover:text-orange-100",
                      {
                        "bg-blue-50 font-bold text-sea-600": isSelected,
                        "font-bold hover:bg-rose-50 hover:text-rose-600": isSelected,
                        "dark:bg-sky-400/20 dark:text-sky-200": isSelected,
                      },
                    )}
                    onClick={() => {
                      dispatch({
                        type: isSelected ? "remove" : "select",
                        indicator: node,
                      });
                    }}
                  >
                    <span className={classNames("line-clamp-2 max-w-52 text-ellipsis")}>{node.abbreviation}</span>

                    <CircleIcon
                      aria-hidden={true}
                      className={classNames(
                        "fill-blue-100 stroke-sea-500",
                        "dark:fill-sky-400/30 dark:stroke-sky-200",
                        "size-4",
                        {
                          "opacity-50 group-hover:opacity-100": !isSelected,
                          "fill-sea-300/60 stroke-sea-600": isSelected,
                          "fill-slate-100 stroke-slate-600": !isSelected,
                          "group-hover:fill-sea-300/60 group-hover:stroke-sea-600 dark:group-hover:fill-orange-400/10 dark:group-hover:stroke-orange-300":
                            !isSelected,
                          "group-hover:fill-rose-200 group-hover:stroke-rose-600": isSelected,
                          "dark:group-hover:fill-orange-400/10 dark:group-hover:stroke-orange-300": isSelected,
                        },
                      )}
                      absoluteStrokeWidth={true}
                      strokeWidth={2}
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </ScrollArea>

        {(activeFilters.length > 0 || pendingSelectedFilters.length > 0) && (
          <div className="flex items-center justify-between px-2">
            <button
              onClick={() => {
                dispatch({ type: "reset" });
                onSelectMany([]);
                setIsOpen(false);
              }}
              type="button"
              className={classNames(
                "flex w-full items-center justify-center rounded-xl bg-slate-50 py-1.5 text-sm font-bold text-slate-600",
                "text-rose-700 hover:bg-rose-100",
                "dark:bg-slate-700/50 dark:text-slate-300",
                "dark:text-rose-200 dark:hover:bg-rose-400/30",
              )}
            >
              <FormattedMessage defaultMessage="Remover Todos" id="SM3nHx" />
            </button>
          </div>
        )}

        {false && (
          <>
            <Separator className="hidden lg:flex" />

            <div className="hidden items-center justify-between px-2 lg:flex">
              <p
                className={classNames(
                  "flex items-center gap-x-0.5 text-sm font-medium tracking-tight text-slate-600 dark:text-slate-400",
                )}
              >
                <LightbulbIcon className="inline-flex size-4" />
                <FormattedMessage
                  defaultMessage="Segure {ctrl} para selecionar vários"
                  id="46orZ7"
                  values={{
                    ctrl: (
                      <kbd className="rounded-md bg-slate-100 px-1 font-mono45 text-xs font-bold dark:bg-slate-700 dark:text-white">
                        Ctrl
                      </kbd>
                    ),
                  }}
                />
              </p>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
